var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import styles from './OrderDetailsPage.module.css';
import { useEffect, useMemo, useState } from 'react';
import { PageLayout } from '../../Components/PageLayout';
import { TextMainInfo, TextMainInfoWithIcon, } from '../../Components/BaseComponents/Typography';
import { useApplication, useOrder } from '../../Contexts/ApplicationContext';
import { TextToCopy } from '../../Components/BaseComponents/TextToCopy';
import { TotalFeeInfoIcon } from '../../Components/Icons/TotalFeeInfoIcon';
import { Status, SwipeluxLogo, TextMainInfoGrey, TransactionLink, } from './styles';
import { actionLabel, middleDots } from './utils';
import { useTheme } from 'styled-components';
import { useNFT } from '../../Contexts/SettingsContext';
import { useMerchant } from '../../Contexts/MerchantContext';
import { Page } from '../../../Services/controllers/StepController';
import { sendMessageToHost } from '../../../utils/sendMessageToHost';
import { MessageTypes } from 'common';
import { observer } from 'mobx-react-lite';
import { useTransactionType } from '../../hooks/useSignalTransaction';
import { storage } from '../../../Services/storage';
import { Tooltip } from '../../Components/BaseComponents/Tooltip';
import { WarningIcons } from '../KYC/components/Icons';
import { addZeroIdToMetamask, currentOrderCreateZeroID, } from '../../../Services/zeroid';
import { FeedbackModal } from './FeedbackModal';
var publicStatusStep = new Map()
    .set(Page.ORDER_PLACED, 'processing')
    .set(Page.FAILED, 'failed')
    .set(Page.SUCCESS, 'success');
export var OrderDetailsPage = observer(function () {
    var _a;
    var order = useOrder();
    var _b = useApplication(), exchangeInfo = _b.orderFacade.exchangeInfo, stepController = _b.stepController;
    var redirectLink = useMerchant().redirectLink;
    var theme = useTheme();
    var smartContractData = useNFT();
    var type = useTransactionType();
    var _c = useState(false), loading = _c[0], setLoading = _c[1];
    var _d = useState(''), error = _d[0], setError = _d[1];
    var _e = useState(false), openFeedbackModal = _e[0], setOpenFeedbackModal = _e[1];
    var _f = useState(false), attemptAddZeroId = _f[0], setAttemptAddZeroId = _f[1];
    var message = 'message' in stepController.currentStep
        ? stepController.currentStep.message
        : undefined;
    useEffect(function () {
        if (redirectLink === null || redirectLink === void 0 ? void 0 : redirectLink.url) {
            sendMessageToHost({
                type: MessageTypes.CREATE_REDIRECT,
                payload: { url: redirectLink.url },
            });
        }
    }, [redirectLink]);
    var statusColorsMap = {
        processing: theme.colors.processing,
        success: theme.colors.success,
        failed: theme.colors.warning,
    };
    var publicStatus = ((_a = order === null || order === void 0 ? void 0 : order.publicStatus) === null || _a === void 0 ? void 0 : _a.toLowerCase()) ||
        publicStatusStep.get(order.currentStep.step) ||
        undefined;
    var uid = 'uid' in order ? order.uid.split('-').join(' - ') : '';
    var createdDate = (order === null || order === void 0 ? void 0 : order.createdAt)
        ? new Date(order === null || order === void 0 ? void 0 : order.createdAt).toLocaleString()
        : '';
    var totalFee = exchangeInfo.totalFeeString;
    var fromAmount = exchangeInfo.payAmountString;
    var realFromAmount = exchangeInfo.payRealAmountString;
    var toAmount = smartContractData
        ? smartContractData.name
        : exchangeInfo.receiveAmountString;
    var userWallet = useMemo(function () {
        var _a, _b;
        if (!('userInfo' in order)) {
            throw Error('No order info');
        }
        return [middleDots((_a = order.userInfo) === null || _a === void 0 ? void 0 : _a.address), (_b = order.userInfo) === null || _b === void 0 ? void 0 : _b.address];
    }, [order]);
    var transactionLinkUrl = useMemo(function () {
        if (!('uid' in order)) {
            // eslint-disable-next-line no-console
            console.error('uid is not defined');
            return;
        }
        return "".concat(process.env.REACT_APP_ORDER_TRACK_PAGE, "?id=").concat(order.uid);
    }, [order]);
    var clearSession = function () {
        storage.clearAll();
        window.location.reload();
    };
    var needAddZeroId = currentOrderCreateZeroID() && !attemptAddZeroId && window.ethereum;
    var onSubmit = function () {
        if (!needAddZeroId) {
            return clearSession();
        }
        setLoading(true);
        setError('');
        addZeroIdToMetamask().finally(function () {
            setAttemptAddZeroId(true);
            setLoading(false);
        });
    };
    useEffect(function () {
        if (order.currentStep.step === Page.SUCCESS) {
            setOpenFeedbackModal(true);
        }
    }, [order.currentStep.step]);
    return (_jsxs(_Fragment, { children: [_jsx(FeedbackModal, { isOpen: openFeedbackModal, onClose: function () { return setOpenFeedbackModal(false); } }), _jsxs(PageLayout, __assign({ footer: {
                    actionButtonLabel: actionLabel(type, needAddZeroId),
                    buttonClick: onSubmit,
                    actionButtonLoader: loading,
                    actionBtnError: error,
                }, hideHeaderChip: true, hideBackArrow: true, headerChildren: _jsx(SwipeluxLogo, {}) }, { children: [_jsxs("div", __assign({ className: styles.details }, { children: [_jsxs("div", __assign({ style: { display: 'flex', gap: 8 } }, { children: [_jsx("h2", __assign({ className: styles.title }, { children: "Order details" })), message ? (_jsx(Tooltip, __assign({ top: 32, defaultVisible: true, content: _jsx("div", { children: message.content }), width: "325px", position: "bottom" }, { children: _jsx(WarningIcons, {}) }))) : null] })), publicStatus && (_jsx(Status, __assign({ statusColor: statusColorsMap[publicStatus] }, { children: publicStatus })))] })), _jsx(TextMainInfoGrey, __assign({ isInfo: true }, { children: createdDate })), _jsx(TextMainInfo, __assign({ style: { fontWeight: 600, margin: '20px 0 8px 0' } }, { children: "Order ID" })), _jsx(TransactionLink, __assign({ href: transactionLinkUrl }, { children: uid })), _jsxs("div", __assign({ className: styles.information }, { children: [_jsxs("div", __assign({ className: styles.information_row }, { children: [_jsx(TextMainInfoGrey, { children: "You received" }), _jsx(TextMainInfo, __assign({ style: { fontWeight: 600, textAlign: 'right' } }, { children: toAmount }))] })), _jsxs("div", __assign({ className: styles.information_row }, { children: [_jsx(TextMainInfoGrey, { children: "Your wallet" }), _jsx(TextToCopy, __assign({ textToCopy: "".concat(userWallet[1]) }, { children: _jsx(TextMainInfo, __assign({ style: { fontWeight: 600, textAlign: 'right' } }, { children: userWallet[0] })) }))] })), _jsxs("div", __assign({ className: styles.information_row }, { children: [_jsxs(TextMainInfoWithIcon, { children: [_jsx(TextMainInfoGrey, { children: "Total fee" }), _jsx(TotalFeeInfoIcon, {})] }), _jsxs(TextMainInfo, __assign({ style: { fontWeight: 600, textAlign: 'right' } }, { children: [realFromAmount ? '≈ ' : null, totalFee] }))] })), _jsxs("div", __assign({ className: styles.information_row }, { children: [_jsx(TextMainInfoGrey, { children: "Exchange rate" }), _jsx(TextToCopy, __assign({ textToCopy: "".concat(exchangeInfo.rate) }, { children: _jsx(TextMainInfo, __assign({ style: { fontWeight: 600, textAlign: 'right' } }, { children: exchangeInfo.rateString })) }))] }))] })), _jsxs("div", __assign({ className: styles.total_block }, { children: [_jsxs("div", __assign({ className: styles.information_row }, { children: [_jsx(TextMainInfoGrey, { children: realFromAmount ? 'Total paid amount' : 'Total payment' }), _jsx(TextMainInfo, __assign({ style: { fontWeight: 600, textAlign: 'right' } }, { children: fromAmount }))] })), realFromAmount ? (_jsxs("div", __assign({ className: styles.information_row }, { children: [_jsx(TextMainInfoGrey, { children: "You charged" }), _jsx(TextMainInfo, __assign({ style: { fontWeight: 600, textAlign: 'right' } }, { children: realFromAmount }))] }))) : null] }))] }))] }));
});
